<template>
  <div>
    <h1 class="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">รายงาน</h1>
    
    <!-- Date Filter -->
    <div class="bg-white p-4 sm:p-6 rounded-lg shadow mb-6">
      <h3 class="font-bold text-base sm:text-lg mb-4">เลือกช่วงเวลา</h3>
      <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-end">
        <div>
          <label class="block text-sm text-gray-600 mb-1">วันที่เริ่มต้น</label>
          <input type="date" v-model="startDate" class="border rounded px-3 py-2 text-sm sm:text-base w-full sm:w-auto">
        </div>
        <div>
          <label class="block text-sm text-gray-600 mb-1">วันที่สิ้นสุด</label>
          <input type="date" v-model="endDate" class="border rounded px-3 py-2 text-sm sm:text-base w-full sm:w-auto">
        </div>
        <button 
          @click="fetchReports" 
          :disabled="loading"
          class="bg-primary text-white px-6 py-2 rounded hover:bg-primary-dark text-sm sm:text-base disabled:opacity-50 flex items-center gap-2"
        >
          <svg v-if="loading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ loading ? 'กำลังโหลด...' : 'ค้นหา' }}
        </button>
      </div>
      
      <!-- Quick Date Buttons -->
      <div class="flex flex-wrap gap-2 mt-4">
        <button @click="setDateRange('today')" class="text-xs px-3 py-1 rounded border hover:bg-gray-100">วันนี้</button>
        <button @click="setDateRange('week')" class="text-xs px-3 py-1 rounded border hover:bg-gray-100">7 วันล่าสุด</button>
        <button @click="setDateRange('month')" class="text-xs px-3 py-1 rounded border hover:bg-gray-100">30 วันล่าสุด</button>
        <button @click="setDateRange('thisMonth')" class="text-xs px-3 py-1 rounded border hover:bg-gray-100">เดือนนี้</button>
        <button @click="setDateRange('year')" class="text-xs px-3 py-1 rounded border hover:bg-gray-100">ปีนี้</button>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div class="bg-white p-4 rounded-lg shadow">
        <p class="text-gray-500 text-sm">ยอดขายรวม</p>
        <p class="text-xl sm:text-2xl font-bold text-green-600">฿{{ formatNumber(summaryTotalSales) }}</p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow">
        <p class="text-gray-500 text-sm">จำนวนคำสั่งซื้อ</p>
        <p class="text-xl sm:text-2xl font-bold text-blue-600">{{ totalOrders }}</p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow">
        <p class="text-gray-500 text-sm">ยอดเฉลี่ย/วัน</p>
        <p class="text-xl sm:text-2xl font-bold text-purple-600">฿{{ formatNumber(averageDailySales) }}</p>
      </div>
      <div class="bg-white p-4 rounded-lg shadow">
        <p class="text-gray-500 text-sm">สินค้าขายดี</p>
        <p class="text-xl sm:text-2xl font-bold text-amber-600 truncate">{{ topProduct }}</p>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
      <!-- Sales Chart -->
      <div class="bg-white p-4 sm:p-6 rounded-lg shadow">
        <div class="flex justify-between items-center mb-4">
          <h3 class="font-bold text-base sm:text-lg">ยอดขายรายวัน</h3>
          <button @click="exportSalesCsv" class="text-xs sm:text-sm text-green-600 hover:text-green-800 flex items-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
            Export CSV
          </button>
        </div>
        <div class="overflow-x-auto">
          <div v-if="salesData.length === 0" class="text-center py-8 text-gray-500">
            <svg class="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p>ไม่มีข้อมูลยอดขายในช่วงเวลานี้</p>
          </div>
          <table v-else class="min-w-full text-sm">
            <thead>
              <tr class="text-left text-xs font-medium text-gray-500 uppercase border-b">
                <th class="pb-3">วันที่</th>
                <th class="pb-3 text-right">ยอดขาย</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in salesData" :key="item.date" class="border-b last:border-0 hover:bg-gray-50">
                <td class="py-2">{{ formatDate(item.date) }}</td>
                <td class="py-2 text-right font-medium text-green-600">฿{{ formatNumber(item.total) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="font-bold bg-gray-50">
                <td class="py-2">รวม</td>
                <td class="py-2 text-right text-green-600">฿{{ formatNumber(summaryTotalSales) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Best Sellers -->
      <div class="bg-white p-4 sm:p-6 rounded-lg shadow">
        <div class="flex justify-between items-center mb-4">
          <h3 class="font-bold text-base sm:text-lg">สินค้าขายดี Top 5</h3>
          <button @click="exportBestSellersCsv" class="text-xs sm:text-sm text-green-600 hover:text-green-800 flex items-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
            Export CSV
          </button>
        </div>
        <div class="overflow-x-auto">
          <div v-if="bestSellers.length === 0" class="text-center py-8 text-gray-500">
            <svg class="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            <p>ไม่มีข้อมูลสินค้าขายดีในช่วงเวลานี้</p>
          </div>
          <div v-else class="space-y-3">
            <div v-for="(item, index) in bestSellers" :key="item.product_name" class="flex items-center gap-3">
              <span :class="getRankClass(index)" class="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {{ index + 1 }}
              </span>
              <div class="flex-1 min-w-0">
                <p class="font-medium text-gray-800 truncate">{{ item.product_name }}</p>
                <p class="text-sm text-gray-500">ขายได้ {{ item.total_quantity }} ชิ้น</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Order Report Table -->
    <div class="bg-white p-4 sm:p-6 rounded-lg shadow">
      <div class="flex justify-between items-center mb-4">
        <h3 class="font-bold text-base sm:text-lg">สรุปคำสั่งซื้อรายวัน</h3>
        <button @click="exportOrdersCsv" class="text-xs sm:text-sm text-green-600 hover:text-green-800 flex items-center gap-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
          Export CSV
        </button>
      </div>
      <div class="overflow-x-auto">
        <div v-if="orderData.length === 0" class="text-center py-8 text-gray-500">
          <svg class="w-12 h-12 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p>ไม่มีข้อมูลคำสั่งซื้อในช่วงเวลานี้</p>
        </div>
        <table v-else class="min-w-full text-sm">
          <thead>
            <tr class="text-left text-xs font-medium text-gray-500 uppercase border-b">
              <th class="pb-3">วันที่</th>
              <th class="pb-3 text-center">จำนวน Order</th>
              <th class="pb-3 text-right">ยอดรวม</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in orderData" :key="item.date" class="border-b last:border-0 hover:bg-gray-50">
              <td class="py-2">{{ formatDate(item.date) }}</td>
              <td class="py-2 text-center">{{ item.count }}</td>
              <td class="py-2 text-right font-medium">฿{{ formatNumber(item.total) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="font-bold bg-gray-50">
              <td class="py-2">รวมทั้งหมด</td>
              <td class="py-2 text-center">{{ totalOrders }}</td>
              <td class="py-2 text-right">฿{{ formatNumber(totalAmount) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import api from '../../api';
import { useNotification } from '../../composables/useNotification';

const { showSuccess, showError, handleApiError } = useNotification();

// Default to this year
const now = new Date();
const startDate = ref(`${now.getFullYear()}-01-01`);
const endDate = ref(now.toISOString().split('T')[0]);

const salesData = ref([]);
const bestSellers = ref([]);
const orderData = ref([]);
const loading = ref(false);

const totalOrders = computed(() => 
  orderData.value.reduce((sum, item) => sum + item.count, 0)
);

const totalAmount = computed(() => 
  orderData.value.reduce((sum, item) => sum + parseFloat(item.total || 0), 0)
);

const summaryTotalSales = computed(() => 
  salesData.value.reduce((sum, item) => sum + parseFloat(item.total || 0), 0)
);

const averageDailySales = computed(() => {
  if (salesData.value.length === 0) return 0;
  return summaryTotalSales.value / salesData.value.length;
});

const topProduct = computed(() => {
  if (bestSellers.value.length === 0) return '-';
  return bestSellers.value[0]?.product_name || '-';
});

const setDateRange = (range) => {
  const today = new Date();
  let start = new Date();
  
  switch (range) {
    case 'today':
      start = today;
      break;
    case 'week':
      start.setDate(today.getDate() - 7);
      break;
    case 'month':
      start.setDate(today.getDate() - 30);
      break;
    case 'thisMonth':
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case 'year':
      start = new Date(today.getFullYear(), 0, 1);
      break;
  }
  
  startDate.value = start.toISOString().split('T')[0];
  endDate.value = today.toISOString().split('T')[0];
  fetchReports();
};

const fetchReports = async () => {
  loading.value = true;
  try {
    const params = { start_date: startDate.value, end_date: endDate.value };
    
    const [salesRes, bestSellersRes, ordersRes] = await Promise.all([
      api.get('/admin/reports/sales', { params }),
      api.get('/admin/reports/best-sellers', { params }),
      api.get('/admin/reports/orders', { params })
    ]);
    
    salesData.value = salesRes.data.data || [];
    bestSellers.value = bestSellersRes.data.data || [];
    orderData.value = ordersRes.data.data || [];
  } catch (error) {
    handleApiError(error);
  } finally {
    loading.value = false;
  }
};

const getRankClass = (index) => {
  const classes = ['bg-yellow-500', 'bg-gray-400', 'bg-amber-600', 'bg-gray-500', 'bg-gray-500'];
  return classes[index] || 'bg-gray-500';
};

const formatNumber = (num) => {
  return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0);
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
};

const downloadCsv = (data, filename) => {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const exportSalesCsv = () => {
  if (salesData.value.length === 0) {
    showError('ไม่มีข้อมูลให้ส่งออก');
    return;
  }
  const headers = ['วันที่', 'ยอดขายรวม'];
  const rows = salesData.value.map(item => [item.date, item.total]);
  const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  downloadCsv(csv, `sales_${startDate.value}_${endDate.value}.csv`);
  showSuccess('ส่งออกยอดขายเรียบร้อยแล้ว');
};

const exportBestSellersCsv = () => {
  if (bestSellers.value.length === 0) {
    showError('ไม่มีข้อมูลให้ส่งออก');
    return;
  }
  const headers = ['สินค้า', 'จำนวนที่ขายได้'];
  const rows = bestSellers.value.map(item => [`"${item.product_name}"`, item.total_quantity]);
  const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  downloadCsv(csv, `best_sellers_${startDate.value}_${endDate.value}.csv`);
  showSuccess('ส่งออกสินค้าขายดีเรียบร้อยแล้ว');
};

const exportOrdersCsv = () => {
  if (orderData.value.length === 0) {
    showError('ไม่มีข้อมูลให้ส่งออก');
    return;
  }
  const headers = ['วันที่', 'จำนวน Order', 'ยอดรวม'];
  const rows = orderData.value.map(item => [item.date, item.count, item.total]);
  rows.push(['รวมทั้งหมด', totalOrders.value, totalAmount.value]);
  const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  downloadCsv(csv, `orders_${startDate.value}_${endDate.value}.csv`);
  showSuccess('ส่งออกรายการคำสั่งซื้อเรียบร้อยแล้ว');
};

onMounted(() => {
  fetchReports();
});
</script>
